import type { AIContentLength } from '../extViewmodels/sdk/models';

export type AICampaignGenerationStatus = 'Queued' | 'InProgress' | 'Complete' | 'PartialSuccess' | 'Failed';

export const TERMINAL_AI_CAMPAIGN_STATUSES: AICampaignGenerationStatus[] = ['Complete', 'PartialSuccess', 'Failed'];

export interface IAICampaignBrief {
	goal: string;
	prompt?: string | null;
	researchInstructions?: string | null;
	webSearchEnabled: boolean;
	subjectGuidance?: string | null;
	skipRecentlyContactedDays?: number | null;
	sourceTemplateId?: string | null;
	generationJobId?: string | null;
	lastGeneratedDate?: string | null;
	campaignResearch?: string | null;
	failedContactCount?: number | null;
	skippedContactCount?: number | null;
	mustInclude?: string[] | null;
	style?: { tone?: string | null; length?: AIContentLength | null } | null;
	revision?: number;
}

export interface IAICampaignSendFrom {
	mode: string;
	selectedUserId?: string;
	connectionType?: string;
	sendFromEmailAddress?: string;
}

export interface IAICampaignSample {
	contactId: string;
	contactName?: string | null;
	subject?: string | null;
	body?: string | null;
	status: 'Generated' | 'Skipped' | 'Failed';
	skipReason?: string | null;
	reasoning?: string | null;
	webFindings?: string | null;
	searchCount: number;
}

export interface IAICampaignGenerationJob {
	jobId: string;
	bulkEmailMessageId: string;
	status: AICampaignGenerationStatus;
	percentComplete: number;
	totalContacts: number;
	processedContacts: number;
	generatedCount: number;
	/** A skip is a correct outcome, not an error. */
	skippedCount: number;
	failedCount: number;
	webSearchEnabled: boolean;
	realizedSearchCount: number;
	currentContactName?: string | null;
	processingStartedUtc?: string | null;
	processingCompletedUtc?: string | null;
	errorMessage?: string | null;
}

/** The web-research figures are a subset of the totals, not additional to them. */
export interface IAICampaignQuota {
	totalUsed: number;
	totalLimit: number;
	totalRemaining: number;
	webResearchUsed: number;
	webResearchLimit: number;
	webResearchRemaining: number;
	perCampaignLimit: number;
	availableWithWebResearch: number;
	availableWithoutWebResearch: number;
	/**
	 * Earliest send the schedule endpoint will accept, because each recipient's email is written in the window
	 * before it. Read this rather than computing a local equivalent: a second copy of the number is how the create
	 * and reschedule screens came to disagree.
	 */
	earliestSendDate: string;
}

/**
 * Lead time an AI campaign is presented as needing. Each recipient's email is written in the window before the send,
 * so anything sooner leaves no time to write them and none for the sender to read them.
 *
 * Two whole days rather than the server's tighter floor: it reads better and is the number the messaging has always
 * quoted. The floor still wins where it is later, which is what earliestAICampaignSend is for.
 */
export const AI_CAMPAIGN_MINIMUM_LEAD_HOURS = 48;

/**
 * The earliest send to offer, taking the later of the presented lead time and the floor the schedule endpoint
 * actually enforces. Shared so the create and reschedule screens cannot drift: keeping separate copies of this
 * number is exactly how one of them ended up with no check at all.
 */
export const earliestAICampaignSend = (quota?: IAICampaignQuota): Date => {
	const presented = new Date();
	presented.setHours(presented.getHours() + AI_CAMPAIGN_MINIMUM_LEAD_HOURS);

	const serverFloor = quota?.earliestSendDate ? new Date(quota.earliestSendDate) : null;
	return serverFloor && serverFloor > presented ? serverFloor : presented;
};

/**
 * Whether scheduling this campaign has to leave room for its emails to be written. Once generation has committed
 * them, moving the send closer is legitimate, and the server keys its own check off the same field.
 */
export const aiCampaignNeedsGenerationLead = (campaign?: { aiBrief?: IAICampaignBrief | null }): boolean =>
	!!campaign?.aiBrief && !campaign.aiBrief.lastGeneratedDate;

export const availableForBrief = (
	quota: IAICampaignQuota | undefined,
	webSearchEnabled: boolean
): number | undefined => {
	if (!quota) {
		return undefined;
	}

	return webSearchEnabled ? quota.availableWithWebResearch : quota.availableWithoutWebResearch;
};

/**
 * Written: has an email and is sent. Failed: writing failed. Skipped: the AI left them out. NotWritten: no email and
 * no recorded reason, which is a run still going or a campaign written before outcomes were recorded.
 */
export type AICampaignRecipientStatus = 'Written' | 'Failed' | 'Skipped' | 'NotWritten';

export interface IAICampaignRecipient {
	contactId: string;
	name?: string | null;
	emailAddress?: string | null;
	subject?: string | null;
	/** False when a recipient has no generated body; the send path refuses to send them. */
	hasContent: boolean;
	status: AICampaignRecipientStatus;
	/** The AI's reason for leaving the contact out; only for Skipped. */
	reason?: string | null;
}

export const groupAICampaignRecipients = (rows: IAICampaignRecipient[]) => {
	const recipients: IAICampaignRecipient[] = [];
	const leftOut: IAICampaignRecipient[] = [];
	let failedCount = 0;
	let skippedCount = 0;
	for (const row of rows) {
		if (row.status === 'Failed' || row.status === 'Skipped') {
			leftOut.push(row);
			if (row.status === 'Failed') {
				failedCount++;
			} else {
				skippedCount++;
			}
		} else {
			recipients.push(row);
		}
	}
	return {
		failedCount,
		leftOut,
		recipients,
		skippedCount,
		writtenCount: recipients.filter(r => r.hasContent).length,
	};
};
